import { easings } from '@react-spring/shared';
import { useSpringRef, useSpring, animated } from '@react-spring/web';
import { uniqueId } from 'lodash';
import React, { MutableRefObject, useRef, useState } from 'react';
import { useClickAway, useMount } from 'react-use';
import { Nullable } from 'src/types/nullable.type';
import { functionNoop } from 'src/utils/function/noop';
import { useEditorStore } from '../../editor-store';
import classNames from 'classnames';
import { ControlPanelShell } from '../control-panel/shell';
import { ThemeColorsContent } from './colors/content';
import { trackSegmentEvent } from 'src/utils/segment';
import {
  DynamicFontChangeHandler,
  INITIAL_DEFAULT_DYNAMIC_FONTS,
  useJourneyDynamicFontStore,
} from 'src/store/journey-dynamic-font';
import { ThemeDynamicFontsContent } from './fonts/dynamic-fonts.content';
import { shallow } from 'zustand/shallow';

const FLOAT_ANIMATION_CONFIG = {
  mass: 1,
  tension: 400,
  friction: 40,
};

export const ThemeSelectionControlPanel = ({ openRef }: { openRef: MutableRefObject<() => void> }) => {
  const themeSettings = useEditorStore((state) => state.themeSettings, shallow);
  const dispatchUserEditorAction = useEditorStore((state) => state.dispatchUserEditorAction);

  const currentDynamicFonts = useJourneyDynamicFontStore((state) => state.current);
  const setCurrentDynamicFonts = useJourneyDynamicFontStore((state) => state.setCurrent);
  const resetCurrentDynamicFonts = useJourneyDynamicFontStore((state) => state.resetCurrent);

  const colorPickerCloseRef = useRef(functionNoop);
  const [isOpen, setIsOpen] = useState(false);
  const [shellKey, setShellKey] = useState(uniqueId());

  const containerElementRef = useRef<Nullable<HTMLDivElement>>(null);
  useClickAway(containerElementRef, () => {
    setIsOpen(false);
    colorPickerCloseRef.current();
  });

  useMount(() => {
    openRef.current = () => {
      setIsOpen(true);
      trackSegmentEvent('Editor Theme Clicked', {});
    };
  });

  const api = useSpringRef();
  const [styles] = useSpring(
    () => ({
      ref: api,
      from: { y: 8, opacity: 0 },
      to: { y: 0, opacity: 1 },
    }),
    []
  );

  React.useEffect(() => {
    if (isOpen) {
      api.start({ from: { y: 8, opacity: 0 }, to: { y: 0, opacity: 1 }, config: FLOAT_ANIMATION_CONFIG });
    } else {
      api.start({
        opacity: 0,
        config: { duration: 200, easing: easings.easeOutCubic },
        onRest: () => {
          setShellKey(uniqueId());
        },
      });
    }
  }, [isOpen]);

  const panelContentRef = React.useRef<HTMLDivElement>(null);

  const onDynamicFontChange: DynamicFontChangeHandler = (type, font) => {
    dispatchUserEditorAction({
      type: 'set-journey-dynamic-font-settings',
      dynamicFontSettings: {
        ...currentDynamicFonts,
        [type]: font,
      },
      themeSettings,
    });

    setCurrentDynamicFonts(type, {
      fontFamily: font.fontFamily,
      variant: font.variant,
      url: font.url,
    });
  };

  const onDynamicFontReset = () => {
    dispatchUserEditorAction({
      type: 'set-journey-dynamic-font-settings',
      dynamicFontSettings: INITIAL_DEFAULT_DYNAMIC_FONTS,
      themeSettings,
    });
    resetCurrentDynamicFonts();
  };

  return (
    <div
      ref={containerElementRef}
      className={classNames('absolute top-full mt-2 right-0', {
        'z-neue-color-panel-selected pointer-events-auto': isOpen,
        'z-neue-color-panel-unselected pointer-events-none': !isOpen,
      })}
    >
      <animated.div style={styles}>
        <ControlPanelShell title='Theme' key={shellKey} size='large' active={isOpen} childrenContainerClassName='-mt-3'>
          <div className='flex flex-col gap-4' ref={panelContentRef}>
            <div className='text-neue-journey-small'>Style your Journey by setting your theme color or font</div>
            <ThemeColorsContent
              closeRef={colorPickerCloseRef}
              panelContentRef={panelContentRef}
              themeSettings={themeSettings}
            />
            <ThemeDynamicFontsContent onChange={onDynamicFontChange} onReset={onDynamicFontReset} />
          </div>
        </ControlPanelShell>
      </animated.div>
    </div>
  );
};
